import React, { useState, useEffect } from "react";
import { Button, Typography } from "@mui/material";
import { Box } from "@mui/system";
import Form from "react-bootstrap/Form";
import useStyles from "./profile.style";
import DashboardMenu from "../../commonComponents/DashboardMenu";
import { getUserData } from "../../../common";
import getUserProfileAPI from "../../../api/getUserProfileAPI";
import updateUserProfileAPI from "../../../api/updateUserProfileAPI";

import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { toast } from "react-toastify";
import { isEmpty, mapValues, pick } from "lodash";
import getCountries from "../../../api/getContriesAPI";
import getStateAPI from "../../../api/getStateAPI";
import { NOT_SAVED_MESSAGE, saveErrorMessage } from "../../../common/saveMessage";
import {
  FIX_FIELDS_MESSAGE,
  phoneValue,
  profileErrors,
} from "../../../common/personValidation";
import "react-toastify/dist/ReactToastify.css";

// what everyone edits; an organiser also has company details
const PERSONAL_FIELDS = [
  "firstName",
  "lastName",
  "email",
  "contactNumber",
  "alternateContactNumber",
];
const COMPANY_FIELDS = [
  "companyName",
  "businessType",
  "bi_tin_ein",
  "title",
  "streetOne",
  "streetTwo",
  "city",
  "state",
  "country",
  "postalCode",
];
// company details that may be saved empty
const OPTIONAL_COMPANY_FIELDS = ["streetTwo", "title"];

// stored value -> text in the box; "no number" is stored as null, or as 0 for
// an organiser (which the server sends as "0")
const boxText = (name, value) => {
  if (value === null || value === undefined) return "";
  if (name === "contactNumber" || name === "alternateContactNumber")
    return Number(value) === 0 ? "" : String(value);
  return String(value);
};

function Profile() {
  const classes = useStyles();
  const [editProfile, SeteditProfile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const userData = getUserData() ?? {};
  // an admin has no company details: they get the personal form, like a
  // bidder
  const isOrganiser = userData.role === "SELLER";
  const fields = isOrganiser
    ? [...PERSONAL_FIELDS, ...COMPANY_FIELDS]
    : PERSONAL_FIELDS;

  // the boxes start with what is stored; an empty password keeps the current
  // one
  const storedValues = (profile) =>
    fields.reduce(
      (values, name) => ({ ...values, [name]: boxText(name, profile?.[name]) }),
      { password: "" }
    );

  const [profileDetail, setProfileDetail] = useState({
    apiState: "loading",
    list: "",
  });

  const [userInfo, setUserInfo] = useState(() => storedValues());

  // the messages come from the current values every render, so none is ever
  // left over; a box shows its message once it was typed in (or on Save)
  const [touched, setTouched] = useState({});
  const errors = pick(profileErrors(userInfo), Object.keys(touched));

  useEffect(() => {
    getProfileData();
  }, []);

  const getProfileData = async () => {
    try {
      const { data } = await getUserProfileAPI();

      if (data) {
        setProfileDetail({ apiState: "success", list: data });
        setUserInfo(storedValues(data));
        setTouched({});
        stateData(data.country);
      } else throw new Error(data);
    } catch (error) {
      console.clear();
      console.log(error);
    }
  };

  const [passwordType, setPasswordType] = useState("password");
  const [passwordInput, setPasswordInput] = useState("");

  const togglePassword = () => {
    if (passwordType === "password") {
      setPasswordType("text");
      console.log("Password type", passwordType);
      return;
    }
    setPasswordType("password");
  };

  // the updater form works on the latest values: the browser filling several
  // boxes at once can't drop one of them
  const handleChange = (event) => {
    const { name, value } = event.target;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  // back to what is stored
  const cancelEdit = () => {
    setUserInfo(storedValues(profileDetail.list));
    setTouched({});
    SeteditProfile(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // check every box again from what is in it now
    if (!isEmpty(profileErrors(userInfo))) {
      setTouched(mapValues(userInfo, () => true));
      toast(FIX_FIELDS_MESSAGE);
      return;
    }

    const payload = {
      firstName: userInfo.firstName.trim(),
      lastName: userInfo.lastName.trim(),
      email: userInfo.email.trim().toLowerCase(),
      contactNumber: phoneValue(userInfo.contactNumber),
      alternateContactNumber: phoneValue(userInfo.alternateContactNumber),
    };
    if (userInfo.password) payload.password = userInfo.password;
    if (isOrganiser) {
      COMPANY_FIELDS.forEach((name) => {
        const value = userInfo[name].trim();
        // a detail the form has no box for (city, state...) is left as stored
        if (value || OPTIONAL_COMPANY_FIELDS.includes(name))
          payload[name] = value;
      });
    }

    try {
      setIsLoading(true);
      const { data } = await updateUserProfileAPI(payload);
      // an organiser's details are also saved with the company
      const saved = isOrganiser ? data?.updatePartner : data?.updateUser;
      if (saved?.[0] === 1) {
        toast("Profile Updated Successfully. !");
        await getProfileData();
        SeteditProfile(false);
      } else {
        toast(NOT_SAVED_MESSAGE);
      }
    } catch (error) {
      console.log(error);
      toast(saveErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  //Get Countries and States
  const [countriesList, updateCountriesList] = useState({
    apiState: "loading",
    list: [],
  });

  const [stateList, updateStateList] = useState({
    apiState: "loading",
    list: [],
  });

  const countryList = async () => {
    try {
      const { data } = await getCountries();

      if (data) {
        if (data) updateCountriesList({ apiState: "success", list: data });
      } else throw new Error(data);
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    countryList();
    return () => {
      return false;
    };
  }, []);

  const stateData = async (countryId) => {
    try {
      console.log(countryId);
      const { data } = await getStateAPI(countryId);

      if (data) {
        if (data) updateStateList({ apiState: "success", list: data });
      } else throw new Error(data);
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    stateData();
    return () => {
      return false;
    };
  }, []);

  console.clear();
  console.log("profileDetail.list", profileDetail.list);

  return (
    <div className={classes.section}>
      <div className="container">
        <div className={classes.Dashboard}>
          <div className={classes.dashboardCard}>
            <DashboardMenu />
            {!isOrganiser ? (
              <form onSubmit={handleSubmit}>
                <div className={classes.dashboardCards}>
                  <Typography className={classes.headText}>
                    Personal Details
                  </Typography>
                  <div className={classes.forms}>
                    <div className="row">
                      <div className="col-md-6">
                        <Box className={classes.forms}>
                          <Form.Group className="mb-3">
                            <Form.Label className={classes.label}>
                              Name
                            </Form.Label>
                            <Form.Control
                              className={classes.input}
                              type="text"
                              name="firstName"
                              placeholder="First Name"
                              required
                              value={userInfo.firstName}
                              onChange={handleChange}
                              disabled={!editProfile}
                            />
                            {errors.firstName && (
                              <span>{errors.firstName}</span>
                            )}
                          </Form.Group>
                        </Box>
                      </div>
                      <div className="col-md-6">
                        <Box className={classes.forms}>
                          <Form.Group className="mb-3">
                            <Form.Label className={classes.label}>
                              Last Name
                            </Form.Label>
                            <Form.Control
                              className={classes.input}
                              type="text"
                              name="lastName"
                              placeholder="Last Name"
                              required
                              value={userInfo.lastName}
                              onChange={handleChange}
                              disabled={!editProfile}
                            />
                            {errors.lastName && <span>{errors.lastName}</span>}
                          </Form.Group>
                        </Box>
                      </div>
                      <div className="col-md-6">
                        <Form.Label className={classes.label}>
                          Contact Number
                        </Form.Label>
                        <Box sx={{ display: "flex" }}>
                          <Form.Control
                            style={{ width: "20%" }}
                            className={classes.input}
                            type="number"
                            placeholder="+91"
                            required
                            disabled={true}
                          />
                          <Form.Control
                            className={classes.input}
                            type="number"
                            name="contactNumber"
                            placeholder="Contact Number"
                            required
                            value={userInfo.contactNumber}
                            onChange={handleChange}
                            disabled={!editProfile}
                          />
                        </Box>
                        {errors.contactNumber && (
                          <span>{errors.contactNumber}</span>
                        )}
                      </div>
                      <div className="col-md-6">
                        <Form.Label className={classes.label}>
                          Alternate Contact Number
                        </Form.Label>
                        <Box sx={{ display: "flex" }}>
                          <Form.Control
                            style={{ width: "20%" }}
                            className={classes.input}
                            type="number"
                            placeholder="+91"
                            required
                            disabled={true}
                          />
                          <Form.Control
                            className={classes.input}
                            type="number"
                            name="alternateContactNumber"
                            placeholder="Alternate Contact Number (optional)"
                            value={userInfo.alternateContactNumber}
                            onChange={handleChange}
                            disabled={!editProfile}
                          />
                        </Box>
                        {errors.alternateContactNumber && (
                          <span>{errors.alternateContactNumber}</span>
                        )}
                      </div>
                      <div className="col-md-6">
                        <Box className={classes.forms}>
                          <Form.Group className="mt-3">
                            <Form.Label className={classes.label}>
                              Email
                            </Form.Label>
                            <Form.Control
                              className={classes.input}
                              type="email"
                              name="email"
                              placeholder="Email"
                              required
                              value={userInfo.email}
                              onChange={handleChange}
                              disabled={!editProfile}
                            />
                            {errors.email && <span>{errors.email}</span>}
                          </Form.Group>
                        </Box>
                      </div>
                      <div className="col-md-6">
                        <Box className={classes.forms}>
                          <Form.Group className="mt-3">
                            <Form.Label className={classes.label}>
                              Password
                            </Form.Label>

                            <div className="row">
                              <div className="col-md-11">
                                <Form.Control
                                  className={classes.input}
                                  type={passwordType}
                                  name="password"
                                  placeholder="Leave empty to keep your password"
                                  disabled={!editProfile}
                                  value={userInfo.password}
                                  onChange={handleChange}
                                  autoComplete="new-password"
                                />
                                <p>
                                  {errors.password && (
                                    <span>{errors.password}</span>
                                  )}
                                </p>
                              </div>
                              <div className="col-md-1">
                                <Box className={classes.forms}>
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={togglePassword}
                                  >
                                    <RemoveRedEyeIcon
                                      style={{ color: "#2D55EB" }}
                                    />
                                  </button>
                                </Box>
                              </div>
                            </div>
                          </Form.Group>
                        </Box>
                      </div>
                      <div className="col-md-6">
                        {editProfile ? (
                          <Box className={classes.saveNextBtns}>
                            <Button
                              onClick={cancelEdit}
                              className={classes.pre}
                            >
                              Cancel
                            </Button>
                            <Button
                              className={classes.next}
                              sx={{ marginLeft: "1em" }}
                              type="submit"
                              disabled={isLoading}
                            >
                              Save
                            </Button>
                          </Box>
                        ) : (
                          <div className={classes.saveBtn}>
                            <Button
                              onClick={() => SeteditProfile(true)}
                              className={classes.next}
                              type="button"
                            >
                              Edit Profile
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <div className={classes.dashboardCards}>
                <Typography className={classes.headText}>
                  Company Information
                </Typography>
                <form onSubmit={handleSubmit}>
                  <div className={classes.forms}>
                    <div className="row">
                      <div className="col-md-6">
                        <Box className={classes.forms}>
                          <Form.Group className="mb-3">
                            <Form.Label className={classes.label}>
                              Company Name
                            </Form.Label>
                            <Form.Control
                              className={classes.input}
                              type="text"
                              name="companyName"
                              placeholder="Company Name"
                              required
                              disabled={!editProfile}
                              value={userInfo.companyName}
                              onChange={handleChange}
                            />
                          </Form.Group>
                        </Box>
                      </div>
                      <div className="col-md-6">
                        <Box className={classes.forms}>
                          <Form.Group className="mb-3">
                            <Form.Label className={classes.label}>
                              Business Type
                            </Form.Label>
                            <Form.Control
                              className={classes.input}
                              type="text"
                              name="businessType"
                              placeholder="Business Type"
                              required
                              disabled={!editProfile}
                              value={userInfo.businessType}
                              onChange={handleChange}
                            />
                          </Form.Group>
                        </Box>
                      </div>
                      <div className="col-md-6">
                        <Box className={classes.forms}>
                          <Form.Group className="mb-3">
                            <Form.Label className={classes.label}>
                              Business Identification Number (BI/TIN/EIN)
                            </Form.Label>
                            <Form.Control
                              className={classes.input}
                              type="text"
                              name="bi_tin_ein"
                              placeholder="Enter Business ID Number"
                              required
                              disabled={!editProfile}
                              value={userInfo.bi_tin_ein}
                              onChange={handleChange}
                            />
                          </Form.Group>
                        </Box>
                      </div>
                      <div className="col-md-6">
                        <Box className={classes.forms}>
                          <Form.Group className="mb-3">
                            <Form.Label className={classes.label}>
                              First Name
                            </Form.Label>
                            <Form.Control
                              className={classes.input}
                              type="text"
                              name="firstName"
                              placeholder="First Name"
                              required
                              disabled={!editProfile}
                              value={userInfo.firstName}
                              onChange={handleChange}
                            />
                            {errors.firstName && (
                              <span>{errors.firstName}</span>
                            )}
                          </Form.Group>
                        </Box>
                      </div>
                      <div className="col-md-6">
                        <Box className={classes.forms}>
                          <Form.Group className="mb-3">
                            <Form.Label className={classes.label}>
                              Last Name
                            </Form.Label>
                            <Form.Control
                              className={classes.input}
                              type="text"
                              name="lastName"
                              placeholder="Last Name"
                              required
                              disabled={!editProfile}
                              value={userInfo.lastName}
                              onChange={handleChange}
                            />
                            {errors.lastName && <span>{errors.lastName}</span>}
                          </Form.Group>
                        </Box>
                      </div>

                      <div className="col-md-12">
                        <div className="row">
                          <Form.Label className={classes.label}>
                            Address
                          </Form.Label>
                          <div className="col-md-6">
                            <Box className={classes.forms}>
                              <Form.Group className="mb-3">
                                <Form.Control
                                  className={classes.input}
                                  type="text"
                                  required
                                  name="streetOne"
                                  placeholder="Street Address"
                                  disabled={!editProfile}
                                  value={userInfo.streetOne}
                                  onChange={handleChange}
                                />
                              </Form.Group>
                            </Box>
                          </div>
                          <div className="col-md-6">
                            <Box className={classes.forms}>
                              <Form.Group className="mb-3">
                                <Form.Label className={classes.label}>
                                  Title
                                </Form.Label>
                                <Form.Control
                                  className={classes.input}
                                  type="text"
                                  name="title"
                                  placeholder="title"
                                  disabled={!editProfile}
                                  value={userInfo.title}
                                  onChange={handleChange}
                                />
                              </Form.Group>
                            </Box>
                          </div>
                          <div className="col-md-6">
                            <Form.Label className={classes.label}>
                              Contact Number
                            </Form.Label>
                            <Box sx={{ display: "flex" }}>
                              <Form.Control
                                style={{ width: "20%" }}
                                className={classes.input}
                                type="number"
                                placeholder="+91"
                                required
                                disabled={true}
                              />
                              <Form.Control
                                className={classes.input}
                                type="number"
                                name="contactNumber"
                                placeholder="Contact Number"
                                required
                                disabled={!editProfile}
                                value={userInfo.contactNumber}
                                onChange={handleChange}
                              />
                            </Box>
                            {errors.contactNumber && (
                              <span>{errors.contactNumber}</span>
                            )}
                          </div>
                          <div className="col-md-6">
                            <Form.Label className={classes.label}>
                              Alternate Contact Number
                            </Form.Label>
                            <Box sx={{ display: "flex" }}>
                              <Form.Control
                                style={{ width: "20%" }}
                                className={classes.input}
                                type="number"
                                placeholder="+91"
                                required
                                disabled={true}
                              />
                              <Form.Control
                                className={classes.input}
                                type="number"
                                name="alternateContactNumber"
                                placeholder="Alternate Contact Number (optional)"
                                disabled={!editProfile}
                                value={userInfo.alternateContactNumber}
                                onChange={handleChange}
                              />
                            </Box>
                            {errors.alternateContactNumber && (
                              <span>{errors.alternateContactNumber}</span>
                            )}
                          </div>
                          <div className="col-md-6">
                            <Box className={classes.forms}>
                              <Form.Group className="">
                                <Form.Label className={classes.label}>
                                  Email
                                </Form.Label>
                                <Form.Control
                                  className={classes.input}
                                  type="text"
                                  name="email"
                                  placeholder="email"
                                  required
                                  disabled={!editProfile}
                                  value={userInfo.email}
                                  onChange={handleChange}
                                />
                                {errors.email && <span>{errors.email}</span>}
                              </Form.Group>
                            </Box>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        {editProfile ? (
                          <Box className={classes.saveNextBtns}>
                            <Button
                              onClick={cancelEdit}
                              className={classes.pre}
                            >
                              Cancel
                            </Button>
                            <Button
                              className={classes.next}
                              sx={{ marginLeft: "1em" }}
                              type="submit"
                              disabled={isLoading}
                            >
                              Save
                            </Button>
                          </Box>
                        ) : (
                          <div className={classes.saveBtn}>
                            <Button
                              onClick={() => SeteditProfile(true)}
                              className={classes.next}
                              type="button"
                            >
                              Edit Profile
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
